import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Menu, X, Home, Store, Hammer, MessageSquare, Archive, Heart, Search, Grid3x3 } from 'lucide-react';
import { useCartWishlist } from '@/context/CartWishlistContext';
import axios from 'axios';
import CategoryNavigation from '@/components/CategoryNavigation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [isCategoryNavOpen, setIsCategoryNavOpen] = useState(false);
  
  const { cartCount, wishlistCount, user, setUser, refreshCounts, setCartCount, setWishlistCount } = useCartWishlist();
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const profileRef = useRef(null);

  // Close search dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchQuery.length >= 2) {
        handleSearch();
      } else {
        setSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileModalOpen(false);
      }
    };
    if (isProfileModalOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileModalOpen]);

  const handleSearch = async () => {
    setSearchLoading(true);
    try {
      const response = await axios.get(`${API}/products/search/suggestions?q=${encodeURIComponent(searchQuery)}&limit=5`);
      setSearchResults(response.data);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearchLoading(false);
    }
  };

  // const handleLogin = () => {
  //   const redirectUrl = `${window.location.origin}/auth-callback`;
  //   window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/login`;
  // };
  const handleLogin = () => {
    // Redirect to your local Google OAuth endpoint
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/login`;
  };

  // const handleLogout = async () => {
  //   try {
  //     await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
  //     setUser(null);
  //     setCartCount(0);
  //     setWishlistCount(0);
  //     navigate('/');
  //   } catch (error) {
  //     console.error('Logout failed:', error);
  //   }
  // };
 const handleLogout = async () => {
  try {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    // Only clear if API succeeded
    localStorage.removeItem('session_token');
    sessionStorage.removeItem('session_token');
    setUser(null);
    setCartCount(0);
    setWishlistCount(0);
    navigate('/');
  } catch (error) {
    console.error('Logout failed:', error);
    // Show user something went wrong
    alert('Logout failed. Please try again.');
  }
};

  const handleSearchResultClick = (productId) => {
    setIsSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
    navigate(`/product/${productId}`);
  };

  return (
    <>
      <nav className="fixed top-0 w-full z-50 glass-effect" data-testid="navbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3" data-testid="logo-link">
              <img 
                src="https://customer-assets.emergentagent.com/job_popcult-bazaar/artifacts/aozrkof5_Collect-I-con%20logo%20black-01.jpg" 
                alt="CollectrsInc Logo" 
                className="h-12 w-12 rounded-full"
                data-testid="logo-image"
              />
              <span className="text-2xl font-bold">
                <span className="accent-red">COLLECTRS</span>
                <span className="text-white">INC.</span>
              </span>
            </Link>

            {/* Desktop Menu - Categories Button */}
            <div className="hidden md:flex items-center space-x-4">
              <button
                onClick={() => setIsCategoryNavOpen(true)}
                className="fixed top-4 left-4 z-50 p-3 bg-black/80 hover:bg-black/90 backdrop-blur-sm rounded-lg border border-white/10 hover:border-red-500 transition-all group"
                data-testid="categories-button"
                aria-label="Open categories menu"
              >
                <svg 
                  width="24" 
                  height="24" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                  className="text-white group-hover:text-red-500 transition-colors"
                >
                  <path 
                    d="M3 12H21M3 6H21M3 18H21" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            {/* Right Side */}
            <div className="hidden md:flex items-center space-x-6">
              {/* Global Search */}
              <div className="relative" ref={searchRef}>
                <button
                  onClick={() => setIsSearchOpen(!isSearchOpen)}
                  className="hover:text-red-500 transition"
                  data-testid="search-icon"
                >
                  <Search size={24} />
                </button>

                {/* Search Dropdown */}
                {isSearchOpen && (
                  <div className="absolute right-0 mt-2 w-80 bg-gray-900 rounded-lg shadow-xl border border-white/10 p-4" data-testid="search-dropdown">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search collectibles..."
                      className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-red-500"
                      data-testid="search-input"
                      autoFocus
                    />

                    {/* Search Results */}
                    {searchLoading && (
                      <div className="mt-2 text-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-red-600 mx-auto"></div>
                      </div>
                    )}

                    {!searchLoading && searchResults.length > 0 && (
                      <div className="mt-2 max-h-96 overflow-y-auto" data-testid="search-results">
                        {searchResults.map((product) => (
                          <button
                            key={product.id}
                            onClick={() => handleSearchResultClick(product.id)}
                            className="w-full flex items-center space-x-3 p-2 hover:bg-white/5 rounded-lg transition text-left"
                            data-testid={`search-result-${product.id}`}
                          >
                            <img src={product.image_url} alt={product.name} className="w-12 h-12 object-cover rounded" />
                            <div className="flex-1">
                              <p className="text-sm font-semibold line-clamp-1">{product.name}</p>
                              <p className="text-xs text-gray-400">{product.category}</p>
                              <p className="text-sm text-red-500 font-bold">₹{product.price.toLocaleString()}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                    {!searchLoading && searchQuery.length >= 2 && searchResults.length === 0 && (
                      <div className="mt-2 text-center py-4 text-gray-400">
                        No results found
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Wishlist Icon */}
              <Link to="/wishlist" className="relative hover:text-red-500 transition" data-testid="wishlist-icon">
                <Heart size={24} />
                {wishlistCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center" data-testid="wishlist-count">
                    {wishlistCount}
                  </span>
                )}
              </Link>

              {/* Cart Icon */}
              <Link to="/cart" className="relative hover:text-red-500 transition" data-testid="cart-icon">
                <ShoppingCart size={24} />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center" data-testid="cart-count">
                    {cartCount}
                  </span>
                )}
              </Link>

              {user ? (
                <div className="relative">
                  <button onClick={() => setIsProfileModalOpen(true)} data-testid="profile-button">
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="h-10 w-10 rounded-full border-2 border-red-500 hover:scale-110 transition" data-testid="user-avatar" />
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-red-600 flex items-center justify-center hover:scale-110 transition" data-testid="user-avatar-placeholder">
                        <User size={20} />
                      </div>
                    )}
                  </button>

                  {/* Profile Modal */}
                  {isProfileModalOpen && (
                    <div 
                      className="absolute right-0 mt-2 bg-gray-900 rounded-xl p-4 w-64 z-50 shadow-xl border border-white/10" 
                      ref={profileRef}
                      onClick={(e) => e.stopPropagation()}
                      data-testid="profile-modal"
                    >
                      {/* User Info */}
                      <div className="text-center mb-4 pb-4 border-b border-white/10">
                        {user?.picture ? (
                          <img src={user.picture} alt={user.name} className="h-12 w-12 rounded-full border-2 border-red-500 mx-auto mb-2" />
                        ) : (
                          <div className="h-12 w-12 rounded-full bg-red-600 flex items-center justify-center mx-auto mb-2">
                            <User size={24} />
                          </div>
                        )}
                        <h2 className="text-sm font-bold truncate">{user?.name}</h2>
                        <p className="text-xs text-gray-400 truncate">{user?.email}</p>
                      </div>

                      {/* Menu Links */}
                      <div className="space-y-1">
                        {[
                          { icon: Store, label: 'Shop', path: '/shop', testid: 'modal-shop' },
                          // { icon: Hammer, label: 'Auctions', path: '/auctions', testid: 'modal-auctions' },
                          // { icon: MessageSquare, label: 'Community', path: '/community', testid: 'modal-community' },
                          // { icon: Archive, label: 'Collections', path: '/collections', testid: 'modal-collections' },
                          { icon: User, label: 'My Profile', path: '/profile', testid: 'modal-profile' }
                        ].map(({ icon: Icon, label, path, testid }) => (
                          <Link
                            key={path}
                            to={path}
                            onClick={() => setIsProfileModalOpen(false)}
                            className="flex items-center space-x-2 p-2 hover:bg-white/5 rounded-lg transition text-sm"
                            data-testid={testid}
                          >
                            <Icon size={16} className="text-red-500" />
                            <span>{label}</span>
                          </Link>
                        ))}
                        
                        <button
                          onClick={() => {handleLogout(); setIsProfileModalOpen(false);}}
                          className="w-full text-left flex items-center space-x-2 p-2 hover:bg-red-600/20 rounded-lg transition text-sm text-red-500 mt-2 pt-3 border-t border-white/10"
                          data-testid="modal-logout"
                        >
                          <span>Logout</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <button onClick={handleLogin} className="btn-primary" data-testid="login-btn">
                  Login
                </button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)} 
              className="md:hidden text-white"
              data-testid="mobile-menu-btn"
            >
              {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-black/95 backdrop-blur-lg" data-testid="mobile-menu">
            <div className="px-4 py-6 space-y-4">
              {/* Categories button INSIDE mobile menu */}
              <button
                onClick={() => {
                  setIsCategoryNavOpen(true);
                  setIsMenuOpen(false);
                }}
                className="w-full text-left py-2 hover:text-red-500 flex items-center gap-2"
                data-testid="mobile-nav-categories"
              >
                <Grid3x3 size={18} />
                Categories
              </button>
              
              <Link to="/" className="block py-2 hover:text-red-500" onClick={() => setIsMenuOpen(false)} data-testid="mobile-nav-home">Home</Link>
              <Link to="/shop" className="block py-2 hover:text-red-500" onClick={() => setIsMenuOpen(false)} data-testid="mobile-nav-shop">Shop</Link>
              <Link to="/auctions" className="block py-2 hover:text-red-500" onClick={() => setIsMenuOpen(false)} data-testid="mobile-nav-auctions">Auctions</Link>
              <Link to="/community" className="block py-2 hover:text-red-500" onClick={() => setIsMenuOpen(false)} data-testid="mobile-nav-community">Community</Link>
              <Link to="/collections" className="block py-2 hover:text-red-500" onClick={() => setIsMenuOpen(false)} data-testid="mobile-nav-collections">Collections</Link>
              <Link to="/wishlist" className="block py-2 hover:text-red-500" onClick={() => setIsMenuOpen(false)} data-testid="mobile-nav-wishlist">
                Wishlist {wishlistCount > 0 && `(${wishlistCount})`}
              </Link>
              <Link to="/cart" className="block py-2 hover:text-red-500" onClick={() => setIsMenuOpen(false)} data-testid="mobile-nav-cart">
                Cart {cartCount > 0 && `(${cartCount})`}
              </Link>
              {user ? (
                <>
                  <Link to="/profile" className="block py-2 hover:text-red-500" onClick={() => setIsMenuOpen(false)} data-testid="mobile-nav-profile">Profile</Link>
                  <button onClick={() => { handleLogout(); setIsMenuOpen(false); }} className="w-full btn-secondary mt-4" data-testid="mobile-logout-btn">Logout</button>
                </>
              ) : (
                <button onClick={handleLogin} className="w-full btn-primary mt-4" data-testid="mobile-login-btn">Login</button>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Category Navigation Modal - Outside nav but inside fragment */}
      <CategoryNavigation 
        isOpen={isCategoryNavOpen}
        onClose={() => setIsCategoryNavOpen(false)}
      />
    </>
  );
};

export default Navbar;